import { useCallback, useEffect, useRef, useState } from 'react'
import api from '@/lib/api'

/** Principal features that can be contextually unlocked during trial. */
export type UnlockableFeature =
  | 'auto_callback'
  | 'auto_follow_up'
  | 'reminder_calls'
  | 'outreach_lists'
  | 'ai_outbound_agent'
  | 'crm_integrations'

export interface TrialUnlockPrompt {
  id: string
  feature: UnlockableFeature
  trigger: string
  headline: string
  body: string
  cta: string
}

export interface TrialUnlocksState {
  isTrialing: boolean
  trialEndsAt: string | null
  /** Features currently unlocked during this org's trial. */
  features: UnlockableFeature[]
  /** Prompt IDs the user explicitly dismissed. */
  dismissed: string[]
  /** Features still locked but eligible for contextual unlock (render "Try during trial" badge). */
  eligibleForUnlock: UnlockableFeature[]
  /** Pending prompts to show the user at natural moments. */
  prompts: TrialUnlockPrompt[]
}

const EMPTY_STATE: TrialUnlocksState = {
  isTrialing: false,
  trialEndsAt: null,
  features: [],
  dismissed: [],
  eligibleForUnlock: [],
  prompts: [],
}

export interface UseTrialUnlocksOptions {
  /** Polling interval in ms. Default 60s. Set to 0 to disable polling. */
  pollMs?: number
  /** If false, hook is inert (use this for unauthenticated routes). */
  enabled?: boolean
}

/**
 * Fetches the current trial-unlock state and exposes enable/dismiss actions.
 *
 * Usage:
 *   const { state, enable, dismiss, markShown, refresh } = useTrialUnlocks()
 */
export function useTrialUnlocks(opts: UseTrialUnlocksOptions = {}) {
  const { pollMs = 60_000, enabled = true } = opts

  const [state, setState] = useState<TrialUnlocksState>(EMPTY_STATE)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inFlight = useRef<Promise<void> | null>(null)

  const refresh = useCallback(async () => {
    if (!enabled) return
    // De-dupe concurrent refreshes
    if (inFlight.current) {
      await inFlight.current
      return
    }
    const p = (async () => {
      setLoading(true)
      try {
        const res = await api.get('/api/billing/trial-unlocks')
        const d = res.data.data
        setState({
          isTrialing: !!d.isTrialing,
          trialEndsAt: d.trialEndsAt ?? null,
          features: d.features ?? [],
          dismissed: d.dismissed ?? [],
          eligibleForUnlock: d.eligibleForUnlock ?? [],
          prompts: d.prompts ?? [],
        })
        setError(null)
      } catch (err: any) {
        // 401/403 or no subscription — treat as "not applicable"
        setState(EMPTY_STATE)
        if (err.response?.status && err.response.status >= 500) {
          setError(err.response?.data?.message ?? err.message ?? 'Failed to load trial unlocks')
        }
      } finally {
        setLoading(false)
      }
    })()
    inFlight.current = p
    try {
      await p
    } finally {
      inFlight.current = null
    }
  }, [enabled])

  // Initial load + polling
  useEffect(() => {
    if (!enabled) return
    refresh()
    if (pollMs <= 0) return
    const timer = window.setInterval(refresh, pollMs)
    return () => window.clearInterval(timer)
  }, [enabled, pollMs, refresh])

  const enable = useCallback(
    async (feature: UnlockableFeature) => {
      await api.post('/api/billing/trial-unlocks/enable', { feature })
      await refresh()
    },
    [refresh]
  )

  const dismiss = useCallback(
    async (promptId: string) => {
      await api.post('/api/billing/trial-unlocks/dismiss', { promptId })
      await refresh()
    },
    [refresh]
  )

  const markShown = useCallback(async (promptId: string) => {
    try {
      await api.post('/api/billing/trial-unlocks/mark-shown', { promptId })
    } catch {
      // non-critical, just prevents re-prompting
    }
  }, [])

  const isFeatureEnabled = useCallback(
    (feature: UnlockableFeature): boolean => state.features.includes(feature),
    [state.features]
  )

  const isEligibleForUnlock = useCallback(
    (feature: UnlockableFeature): boolean => state.eligibleForUnlock.includes(feature),
    [state.eligibleForUnlock]
  )

  return {
    state,
    loading,
    error,
    refresh,
    enable,
    dismiss,
    markShown,
    isFeatureEnabled,
    isEligibleForUnlock,
  }
}
